/**
 * Email Campaign Service
 * Manages campaign creation, status updates, and metric tracking
 *
 * Features:
 * - Create campaigns for batch newsletter sends
 * - Track aggregate metrics (opens, clicks, replies)
 * - Calculate engagement rates and scores
 * - Support campaign comparison and analytics
 *
 * TDD Implementation: Tests written first in EmailCampaignService.test.js
 */

import { generateClient } from 'aws-amplify/api';
import logger from '../utils/logger.js';
import { getCurrentUser } from 'aws-amplify/auth';
import {
  createNetworkError,
  createValidationError
} from '../utils/StandardErrorFormat.js';
import auditService from './AuditService.js';
import billingService from './BillingService.js';

// Valid campaign statuses (UPDATED for unified campaign system)
const VALID_STATUSES = ['DRAFT', 'SCHEDULED', 'SENDING', 'SENT', 'COMPLETED', 'FAILED', 'CANCELLED', 'ARCHIVED'];

// Phase 1C (#169): complianceStatus state machine — see SPEC-COMPLIANCE-APPROVAL.md § 2.1
const VALID_COMPLIANCE_TRANSITIONS = {
  draft:          ['draft', 'auto_approved', 'pending_review'],
  auto_approved:  ['auto_approved'],
  pending_review: ['pending_review', 'approved', 'rejected'],
  approved:       ['approved'],
  rejected:       ['rejected', 'draft'],
};

const assertValidComplianceTransition = (from, to) => {
  const allowed = VALID_COMPLIANCE_TRANSITIONS[from];
  if (!allowed || !allowed.includes(to)) {
    const e = new Error(`INVALID_TRANSITION: complianceStatus '${from}' → '${to}' is not a valid transition`);
    e.code = 'INVALID_TRANSITION';
    throw e;
  }
};

// Valid metrics for incrementing
const VALID_METRICS = [
  'sentCount',
  'deliveredCount',
  'failedCount',
  'openedCount',
  'uniqueOpensCount',
  'clickedCount',
  'uniqueClicksCount',
  'bouncedCount',
  'complainedCount',
  'unsubscribedCount',
  'repliedCount'
];

class EmailCampaignService {
  constructor(client = null) {
    // Create service-specific logger
    this.logger = logger.child('EmailCampaignService');

    // Allow client injection for testing
    this.client = client || generateClient();
  }
  /**
   * Create a new email campaign (UNIFIED: includes content fields)
   *
   * @param {Object} input - Campaign data
   * @param {string} input.userId - User ID
   * @param {string} input.name - Campaign name
   * @param {string} input.subject - Email subject line (optional for DRAFT)
   * @param {string} input.content - Email content (HTML or Markdown, optional for DRAFT)
   * @param {string} input.contentType - Content type: 'HTML' or 'MARKDOWN' (default: HTML)
   * @param {string} input.templateId - Reference to ContentTemplate (optional)
   * @param {Object} input.metadata - AI generation metadata, placeholders, etc. (optional)
   * @param {string} input.newsletterId - DEPRECATED: Legacy newsletter reference
   * @param {Array<string>} input.recipientClientIds - Array of Client IDs (optional for DRAFT)
   * @param {number} input.recipientCount - Number of recipients
   * @param {string} input.status - Campaign status (default: DRAFT)
   * @returns {Promise<Object>} Created campaign
   */
  async createCampaign(input) {
    try {
      // Get current user ID
      const currentUser = await getCurrentUser();
      const userId = currentUser.userId;

      // Validate required fields (relaxed for DRAFT status)
      this.validateCampaignInput({ ...input, userId });

      // Prepare campaign data with default values
      const campaignData = {
        userId,
        name: input.name,

        // Content fields (NEW - unified campaign system)
        subject: input.subject || '',
        content: input.content || '',
        contentType: input.contentType || 'HTML',
        templateId: input.templateId,
        // #330 — series linkage: brief + reviewer resolution keys off the
        // campaign's own series, so it must persist (and survive duplicate).
        seriesId: input.seriesId,
        metadata: input.metadata,

        // Legacy reference (DEPRECATED)
        newsletterId: input.newsletterId,

        recipientClientIds: input.recipientClientIds || [], // Optional for DRAFT
        recipientCount: input.recipientCount || 0,
        status: input.status || 'DRAFT',
        scheduledFor: input.scheduledFor,

        // Initialize all counters to 0
        sentCount: 0,
        deliveredCount: 0,
        failedCount: 0,
        openedCount: 0,
        uniqueOpensCount: 0,
        clickedCount: 0,
        uniqueClicksCount: 0,
        bouncedCount: 0,
        complainedCount: 0,
        unsubscribedCount: 0,
        repliedCount: 0,

        // Initialize all rates to 0
        openRate: 0,
        clickRate: 0,
        clickToOpenRate: 0,
        bounceRate: 0,
        replyRate: 0,
        engagementScore: 0
      };

      this.logger.info('Creating campaign', { name: campaignData.name });

      const response = await this.client.graphql({
        query: `
          mutation CreateEmailCampaign($input: CreateEmailCampaignInput!) {
            createEmailCampaign(input: $input) {
              id
              userId
              newsletterId
              name
              subject
              content
              contentType
              templateId
              seriesId
              metadata
              recipientClientIds
              recipientCount
              status
              scheduledFor
              sentAt
              startedAt
              completedAt
              sentCount
              deliveredCount
              failedCount
              openedCount
              uniqueOpensCount
              clickedCount
              uniqueClicksCount
              bouncedCount
              complainedCount
              unsubscribedCount
              repliedCount
              openRate
              clickRate
              clickToOpenRate
              bounceRate
              replyRate
              engagementScore
              createdAt
              updatedAt
            }
          }
        `,
        variables: { input: campaignData }
      });

      if (response.errors && response.errors.length > 0) {
        throw new Error(`Failed to create campaign: ${response.errors[0].message}`);
      }

      const campaign = response.data?.createEmailCampaign;

      if (!campaign) {
        throw new Error('Failed to create campaign: No data returned');
      }

      this.logger.info('Campaign created', { campaignId: campaign.id });

      // Compliance audit trail (non-blocking — campaign create succeeds even if
      // the audit row write fails, but we log the failure for ops visibility)
      auditService.logAction({
        action: 'NEWSLETTER_GENERATED',
        resourceType: 'CAMPAIGN',
        resourceId: campaign.id,
        content: campaign.content,
        details: { name: campaign.name, recipientCount: campaign.recipientCount, status: campaign.status },
      }).catch((err) => {
        this.logger.error('Audit write failed for NEWSLETTER_GENERATED', {
          campaignId: campaign.id,
          error: err?.message || String(err),
        });
      });

      return campaign;

    } catch (error) {
      this.logger.error('Error creating campaign:', error);
      throw error;
    }
  }

  /**
   * Update campaign (UNIFIED: updates content and settings)
   *
   * @param {string} campaignId - Campaign ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated campaign
   */
  async updateCampaign(campaignId, updates) {
    let updateData;
    try {
      if (!campaignId) {
        throw new Error('Campaign ID is required');
      }

      // Phase 1C (#169): validate complianceStatus transitions
      let priorComplianceStatus = null;
      if (updates && Object.prototype.hasOwnProperty.call(updates, 'complianceStatus')) {
        const current = await this.getCampaign(campaignId);
        priorComplianceStatus = current?.complianceStatus || 'draft';
        assertValidComplianceTransition(priorComplianceStatus, updates.complianceStatus);
      }

      updateData = {
        id: campaignId,
        ...updates
      };

      this.logger.info('Updating campaign', { campaignId });

      const response = await this.client.graphql({
        query: `
          mutation UpdateEmailCampaign($input: UpdateEmailCampaignInput!) {
            updateEmailCampaign(input: $input) {
              id
              userId
              name
              subject
              content
              contentType
              templateId
              metadata
              recipientClientIds
              recipientCount
              status
              scheduledFor
              sentAt
              startedAt
              completedAt
              sentCount
              deliveredCount
              failedCount
              openedCount
              uniqueOpensCount
              clickedCount
              uniqueClicksCount
              bouncedCount
              complainedCount
              unsubscribedCount
              repliedCount
              openRate
              clickRate
              clickToOpenRate
              bounceRate
              replyRate
              engagementScore
              createdAt
              updatedAt
            }
          }
        `,
        variables: { input: updateData }
      });

      if (response.errors && response.errors.length > 0) {
        this.logger.error('GraphQL Errors:', JSON.stringify(response.errors, null, 2));
        throw new Error(`Failed to update campaign: ${response.errors[0].message}`);
      }

      const campaign = response.data?.updateEmailCampaign;

      if (!campaign) {
        this.logger.error('No data returned. Full response:', response);
        throw new Error('Failed to update campaign: No data returned');
      }

      this.logger.info('Campaign updated', { campaignId: campaign.id });

      // Compliance audit trail (non-blocking; log failures for ops visibility)
      auditService.logAction({
        action: 'NEWSLETTER_EDITED',
        resourceType: 'CAMPAIGN',
        resourceId: campaignId,
        content: campaign.content,
        details: { updatedFields: Object.keys(updates) },
      }).catch((err) => {
        this.logger.error('Audit write failed for NEWSLETTER_EDITED', {
          campaignId,
          error: err?.message || String(err),
        });
      });

      // Phase 1C (#169): when transitioning into auto_approved from draft,
      // emit an APPROVE audit row with autoApproved=true marker so the
      // audit trail captures the implicit solo-advisor self-approval.
      if (
        priorComplianceStatus === 'draft' &&
        updates?.complianceStatus === 'auto_approved'
      ) {
        let approvedBy = null;
        try {
          approvedBy = (await getCurrentUser())?.userId;
        } catch {
          // getCurrentUser failure shouldn't block the audit
        }
        await auditService.logAction({
          action: 'APPROVE',
          resourceType: 'CAMPAIGN',
          resourceId: campaignId,
          content: campaign.content,
          details: { approvedBy, autoApproved: true },
        }).catch((err) => {
          this.logger.error('Audit write failed for APPROVE (auto_approved transition)', {
            campaignId,
            error: err?.message || String(err),
          });
        });
      }

      return campaign;

    } catch (error) {
      this.logger.error('Error updating campaign:', error);
      this.logger.error('Update data was:', JSON.stringify(updateData, null, 2));

      // If error contains GraphQL errors array, log details
      if (error.errors && Array.isArray(error.errors)) {
        this.logger.error('GraphQL Error Details:', JSON.stringify(error.errors, null, 2));
        error.errors.forEach((err, idx) => {
          this.logger.error(`Error ${idx + 1}:`, err.message);
          if (err.path) this.logger.error(`  Path:`, err.path);
          if (err.locations) this.logger.error(`  Locations:`, err.locations);
          if (err.extensions) this.logger.error(`  Extensions:`, err.extensions);
        });
      }

      throw error;
    }
  }

  /**
   * Update campaign status
   *
   * @param {string} campaignId - Campaign ID
   * @param {string} status - New status
   * @param {Object} metrics - Optional metrics to update
   * @returns {Promise<Object>} Updated campaign
   */
  async updateCampaignStatus(campaignId, status, metrics = {}) {
    try {
      // Validate inputs
      if (!campaignId) {
        throw new Error('Campaign ID is required');
      }

      if (!VALID_STATUSES.includes(status)) {
        throw new Error(`Invalid campaign status: ${status}. Must be one of: ${VALID_STATUSES.join(', ')}`);
      }

      const updateData = {
        id: campaignId,
        status,
        ...metrics
      };

      // Add timestamps based on status
      if (status === 'IN_PROGRESS' && !updateData.startedAt) {
        updateData.startedAt = new Date().toISOString();
      }

      if (status === 'COMPLETED' && !updateData.completedAt) {
        updateData.completedAt = new Date().toISOString();
      }

      this.logger.info(`Updating campaign ${campaignId} to ${status}`);

      const response = await this.client.graphql({
        query: `
          mutation UpdateEmailCampaign($input: UpdateEmailCampaignInput!) {
            updateEmailCampaign(input: $input) {
              id
              status
              startedAt
              completedAt
              sentCount
              deliveredCount
              failedCount
              updatedAt
            }
          }
        `,
        variables: { input: updateData }
      });

      if (response.errors && response.errors.length > 0) {
        throw new Error(`Failed to update campaign status: ${response.errors[0].message}`);
      }

      const campaign = response.data?.updateEmailCampaign;

      // Compliance audit trail for status changes.
      // Phase 1C (#169): SENT writes 'SEND' so it appears in the
      // ComplianceAuditLog audit chain expected by the spec § 2.4.
      const actionMap = {
        SENDING: 'NEWSLETTER_SENT',
        SENT: 'SEND',
        SCHEDULED: 'SCHEDULE',
        CANCELLED: 'CANCEL',
      };
      const auditAction = actionMap[status] || 'NEWSLETTER_EDITED';
      auditService.logAction({
        action: auditAction,
        resourceType: 'CAMPAIGN',
        resourceId: campaignId,
        details: { newStatus: status, ...metrics },
      }).catch((err) => {
        this.logger.error('Audit write failed for status change', {
          campaignId,
          action: auditAction,
          newStatus: status,
          error: err?.message || String(err),
        });
      });

      // Billing: increment newsletter usage counter when a send is initiated or completed
      if (status === 'SENDING' || status === 'SENT') {
        billingService.incrementNewsletterUsage().catch((err) => {
          this.logger.error('[billing] Failed to increment newsletter usage:', err);
        }); // Non-blocking — never delay campaign operations for billing
      }

      return campaign;

    } catch (error) {
      this.logger.error('Error updating campaign status:', error);
      throw error;
    }
  }

  /**
   * Increment a campaign metric counter
   *
   * @param {string} campaignId - Campaign ID
   * @param {string} metricName - Name of metric to increment
   * @param {number} amount - Amount to increment (default: 1)
   * @returns {Promise<Object>} Updated campaign
   */
  async incrementCampaignMetric(campaignId, metricName, amount = 1) {
    try {
      // Validate inputs
      if (!campaignId) {
        throw new Error('Campaign ID is required');
      }

      if (!VALID_METRICS.includes(metricName)) {
        throw new Error(`Invalid metric name: ${metricName}. Must be one of: ${VALID_METRICS.join(', ')}`);
      }

      // Get current campaign data
      const campaign = await this.getCampaign(campaignId);

      if (!campaign) {
        throw new Error(`Campaign not found: ${campaignId}`);
      }

      // Calculate new value
      const currentValue = campaign[metricName] || 0;
      const newValue = currentValue + amount;

      const updateData = {
        id: campaignId,
        [metricName]: newValue
      };

      this.logger.info(`Incrementing ${metricName} for campaign ${campaignId}: ${currentValue} → ${newValue}`);

      const response = await this.client.graphql({
        query: `
          mutation UpdateEmailCampaign($input: UpdateEmailCampaignInput!) {
            updateEmailCampaign(input: $input) {
              id
              ${metricName}
              updatedAt
            }
          }
        `,
        variables: { input: updateData }
      });

      if (response.errors && response.errors.length > 0) {
        throw new Error(`Failed to increment metric: ${response.errors[0].message}`);
      }

      return response.data?.updateEmailCampaign;

    } catch (error) {
      this.logger.error('Error incrementing metric:', error);
      throw error;
    }
  }

  /**
   * Calculate and update campaign rates
   *
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Updated campaign with calculated rates
   */
  async calculateCampaignRates(campaignId) {
    try {
      // Get current campaign data
      const campaign = await this.getCampaign(campaignId);

      if (!campaign) {
        throw new Error(`Campaign not found: ${campaignId}`);
      }

      const delivered = campaign.deliveredCount || 0;
      const uniqueOpens = campaign.uniqueOpensCount || 0;
      const uniqueClicks = campaign.uniqueClicksCount || 0;
      const replied = campaign.repliedCount || 0;
      const bounced = campaign.bouncedCount || 0;
      const sent = campaign.sentCount || 0;

      // Calculate rates (handle division by zero)
      const openRate = delivered > 0 ? Math.round((uniqueOpens / delivered) * 10000) / 100 : 0;
      const clickRate = delivered > 0 ? Math.round((uniqueClicks / delivered) * 10000) / 100 : 0;
      const clickToOpenRate = uniqueOpens > 0 ? Math.round((uniqueClicks / uniqueOpens) * 10000) / 100 : 0;
      const bounceRate = sent > 0 ? Math.round((bounced / sent) * 10000) / 100 : 0;
      const replyRate = delivered > 0 ? Math.round((replied / delivered) * 10000) / 100 : 0;

      // Calculate engagement score (weighted: opens 40%, clicks 40%, replies 20%)
      const engagementScore = Math.round((openRate * 0.4) + (clickRate * 0.4) + (replyRate * 0.2));

      const updateData = {
        id: campaignId,
        openRate,
        clickRate,
        clickToOpenRate,
        bounceRate,
        replyRate,
        engagementScore
      };

      this.logger.info(`Calculated rates for campaign ${campaignId}:`, {
        openRate,
        clickRate,
        engagementScore
      });

      const response = await this.client.graphql({
        query: `
          mutation UpdateEmailCampaign($input: UpdateEmailCampaignInput!) {
            updateEmailCampaign(input: $input) {
              id
              openRate
              clickRate
              clickToOpenRate
              bounceRate
              replyRate
              engagementScore
              updatedAt
            }
          }
        `,
        variables: { input: updateData }
      });

      if (response.errors && response.errors.length > 0) {
        throw new Error(`Failed to update campaign rates: ${response.errors[0].message}`);
      }

      return response.data?.updateEmailCampaign;

    } catch (error) {
      this.logger.error('Error calculating campaign rates:', error);
      throw error;
    }
  }

  /**
   * Get campaign by ID
   *
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object|null>} Campaign or null if not found (null is valid for non-existent campaigns)
   * @throws {ServiceError} If fetch fails
   */
  async getCampaign(campaignId) {
    try {
      const response = await this.client.graphql({
        query: `
          query GetEmailCampaign($id: ID!) {
            getEmailCampaign(id: $id) {
              id
              userId
              newsletterId
              name
              subject
              # #326 — fields needed for duplicateCampaign. Adding these
              # to the base getCampaign query is additive — callers that
              # ignore them are unaffected; duplicateCampaign reads them.
              contentType
              templateId
              seriesId
              metadata
              recipientClientIds
              recipientCount
              status
              scheduledFor
              sentAt
              startedAt
              completedAt
              sentCount
              deliveredCount
              failedCount
              openedCount
              uniqueOpensCount
              clickedCount
              uniqueClicksCount
              bouncedCount
              complainedCount
              unsubscribedCount
              repliedCount
              openRate
              clickRate
              clickToOpenRate
              bounceRate
              replyRate
              engagementScore
              # Phase 1C — compliance approval (#259 fix)
              # Without these fields the reviewer-side flow is silently
              # broken: updateCampaign reads complianceStatus=undefined,
              # the inbox filter excludes every row, and the review panel
              # renders blank.
              complianceStatus
              content
              rejectionReason
              reviewedByUserId
              reviewedAt
              createdAt
              updatedAt
            }
          }
        `,
        variables: { id: campaignId }
      });

      if (response.errors && response.errors.length > 0) {
        this.logger.error('Error fetching campaign:', response.errors[0].message);
        throw new Error(`GraphQL errors: ${JSON.stringify(response.errors)}`);
      }

      // Returning null for non-existent campaigns is valid (not an error)
      return response.data?.getEmailCampaign || null;

    } catch (error) {
      this.logger.error('Error getting campaign:', error);

      // Re-throw ServiceErrors as-is
      if (error.name === 'ServiceError') {
        throw error;
      }

      // Wrap other errors with user-friendly message
      createNetworkError(`Failed to fetch campaign: ${error.message}`, error);
    }
  }

  /**
   * Get campaigns for a user
   *
   * @param {string} userId - User ID
   * @param {number} limit - Max campaigns to return
   * @returns {Promise<Array>} List of campaigns
   * @throws {ServiceError} If fetch fails
   */
  async getUserCampaigns(userId, limit = 100) {
    try {
      const response = await this.client.graphql({
        query: `
          query ListEmailCampaignsByUser($userId: ID!, $limit: Int) {
            listEmailCampaignsByUser(
              userId: $userId,
              limit: $limit,
              sortDirection: DESC
            ) {
              items {
                id
                userId
                newsletterId
                name
                subject
                status
                recipientCount
                sentCount
                deliveredCount
                openRate
                clickRate
                engagementScore
                # Phase 1C — compliance approval (#259 fix). The reviewer
                # inbox at /app/communications/review filters this list by
                # complianceStatus='pending_review' — without the field
                # in the SELECT set the filter excludes every row.
                complianceStatus
                rejectionReason
                reviewedByUserId
                reviewedAt
                createdAt
              }
            }
          }
        `,
        variables: { userId, limit }
      });

      if (response.errors && response.errors.length > 0) {
        this.logger.error('Error fetching campaigns:', response.errors[0].message);
        throw new Error(`GraphQL errors: ${JSON.stringify(response.errors)}`);
      }

      return response.data?.listEmailCampaignsByUser?.items || [];

    } catch (error) {
      this.logger.error('Error getting user campaigns:', error);
      if (error.errors) {
        this.logger.error('GraphQL errors:', JSON.stringify(error.errors, null, 2));
      }

      // Re-throw ServiceErrors as-is
      if (error.name === 'ServiceError') {
        throw error;
      }

      // Wrap other errors with user-friendly message
      createNetworkError(`Failed to fetch user campaigns: ${error.message}`, error);
    }
  }

  /**
   * Archive a campaign
   *
   * Sets campaign status to 'ARCHIVED' and logs a NEWSLETTER_ARCHIVED audit event.
   *
   * @param {string} campaignId - Campaign ID to archive
   * @returns {Promise<Object>} Updated campaign
   */
  async archiveCampaign(campaignId) {
    if (!campaignId) {
      throw new Error('Campaign ID is required');
    }

    const updateData = {
      id: campaignId,
      status: 'ARCHIVED',
    };

    this.logger.info(`Archiving campaign ${campaignId}`);

    const response = await this.client.graphql({
      query: `
        mutation UpdateEmailCampaign($input: UpdateEmailCampaignInput!) {
          updateEmailCampaign(input: $input) {
            id
            status
            updatedAt
          }
        }
      `,
      variables: { input: updateData },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(`Failed to archive campaign: ${response.errors[0].message}`);
    }

    const campaign = response.data?.updateEmailCampaign;

    // Compliance audit trail — NEWSLETTER_ARCHIVED (non-blocking; log failures)
    auditService.logAction({
      action: 'NEWSLETTER_ARCHIVED',
      resourceType: 'CAMPAIGN',
      resourceId: campaignId,
      details: { status: 'ARCHIVED' },
    }).catch((err) => {
      this.logger.error('Audit write failed for NEWSLETTER_ARCHIVED', {
        campaignId,
        error: err?.message || String(err),
      });
    });

    return campaign;
  }

  /**
   * Delete a campaign (hard delete).
   *
   * Sprint 5 #153 — drafts can pile up during testing/iteration; advisors
   * need a way to remove them. We restrict this to DRAFT status at the call
   * site (CampaignsView surfaces the trash icon only on Draft rows). This
   * service method itself is permissive — pipeline orchestration / archival
   * use \`archiveCampaign\` instead. Hard delete here means the
   * EmailCampaign row is removed; any per-recipient EmailMessage rows are
   * orphaned (acceptable for drafts which never produced messages).
   *
   * @param {string} campaignId
   * @returns {Promise<{id: string}>} the deleted ID
   */
  async deleteCampaign(campaignId) {
    if (!campaignId) {
      throw new Error('Campaign ID is required');
    }

    this.logger.info(`Deleting campaign ${campaignId}`);

    const response = await this.client.graphql({
      query: `
        mutation DeleteEmailCampaign($input: DeleteEmailCampaignInput!) {
          deleteEmailCampaign(input: $input) {
            id
          }
        }
      `,
      variables: { input: { id: campaignId } },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(`Failed to delete campaign: ${response.errors[0].message}`);
    }

    const deleted = response.data?.deleteEmailCampaign;

    // Compliance audit trail — NEWSLETTER_DELETED. Non-blocking; if the audit
    // service is unavailable we still want the delete to land, but we log
    // the failure for ops visibility.
    auditService.logAction({
      action: 'NEWSLETTER_DELETED',
      resourceType: 'CAMPAIGN',
      resourceId: campaignId,
      details: { method: 'hard_delete' },
    }).catch((err) => {
      this.logger.error('Audit write failed for NEWSLETTER_DELETED', {
        campaignId,
        error: err?.message || String(err),
      });
    });

    return deleted || { id: campaignId };
  }

  /**
   * Validate campaign input data
   * @private
   */
  validateCampaignInput(input) {
    // Always required fields
    const alwaysRequired = ['userId', 'name'];

    for (const field of alwaysRequired) {
      if (!input[field]) {
        throw new Error(`Missing required field: ${field}`);
      }
    }

    // Status-dependent validation
    const status = input.status || 'DRAFT';

    if (!VALID_STATUSES.includes(status)) {
      throw new Error(`Invalid status: ${status}. Must be one of: ${VALID_STATUSES.join(', ')}`);
    }

    // For non-DRAFT campaigns, require more fields
    if (status !== 'DRAFT') {
      if (!input.subject) {
        throw new Error('subject is required for non-DRAFT campaigns');
      }
      if (!input.content && !input.newsletterId) {
        throw new Error('content or newsletterId is required for non-DRAFT campaigns');
      }
      if (!input.recipientClientIds || input.recipientClientIds.length === 0) {
        throw new Error('recipientClientIds is required for non-DRAFT campaigns');
      }
      if (typeof input.recipientCount !== 'number' || input.recipientCount <= 0) {
        throw new Error('recipientCount must be a positive number for non-DRAFT campaigns');
      }
    }

    // Validate contentType if provided
    if (input.contentType && !['HTML', 'MARKDOWN'].includes(input.contentType)) {
      throw new Error('contentType must be either HTML or MARKDOWN');
    }
  }

  // ==========================================================================
  // Phase 1C (#169) — compliance approval methods
  // See docs/03-technical/SPEC-COMPLIANCE-APPROVAL.md § 2.3
  // ==========================================================================

  /**
   * Submit a draft campaign for reviewer approval (team flow).
   * Transition: draft → pending_review.
   * Does NOT write an audit row (no human action by reviewer yet).
   */
  async submitForReview(campaignId) {
    if (!campaignId) throw new Error('Campaign ID is required');
    return this.updateCampaign(campaignId, {
      complianceStatus: 'pending_review',
      reviewedByUserId: null,
      reviewedAt: null,
    });
  }

  /**
   * Reviewer approves a pending_review campaign.
   * Transition: pending_review → approved.
   * Writes APPROVE audit row.
   */
  async approveCampaign(campaignId) {
    if (!campaignId) throw new Error('Campaign ID is required');

    let approvedBy = null;
    try {
      approvedBy = (await getCurrentUser())?.userId;
    } catch {
      // proceed without approver id
    }

    const result = await this.updateCampaign(campaignId, {
      complianceStatus: 'approved',
      reviewedByUserId: approvedBy,
      reviewedAt: new Date().toISOString(),
    });

    await auditService.logAction({
      action: 'APPROVE',
      resourceType: 'CAMPAIGN',
      resourceId: campaignId,
      content: result?.content,
      details: { approvedBy, autoApproved: false },
    }).catch((err) => {
      this.logger.error('Audit write failed for APPROVE (reviewer)', {
        campaignId,
        error: err?.message || String(err),
      });
    });

    return result;
  }

  /**
   * Reviewer rejects a pending_review campaign with a reason.
   * Transition: pending_review → rejected.
   * Throws REJECTION_REASON_REQUIRED if reason is empty/whitespace/null.
   * Writes REJECT audit row.
   */
  async rejectCampaign(campaignId, reason) {
    if (!campaignId) throw new Error('Campaign ID is required');
    if (!reason || typeof reason !== 'string' || reason.trim().length === 0) {
      const e = new Error('REJECTION_REASON_REQUIRED: rejection reason must be a non-empty string');
      e.code = 'REJECTION_REASON_REQUIRED';
      throw e;
    }

    let rejectedBy = null;
    try {
      rejectedBy = (await getCurrentUser())?.userId;
    } catch {
      // proceed without rejector id
    }

    const result = await this.updateCampaign(campaignId, {
      complianceStatus: 'rejected',
      rejectionReason: reason,
      reviewedByUserId: rejectedBy,
      reviewedAt: new Date().toISOString(),
    });

    await auditService.logAction({
      action: 'REJECT',
      resourceType: 'CAMPAIGN',
      resourceId: campaignId,
      content: result?.content,
      details: { rejectedBy, reason },
    }).catch((err) => {
      this.logger.error('Audit write failed for REJECT', {
        campaignId,
        error: err?.message || String(err),
      });
    });

    return result;
  }

  /**
   * #326 — Clone an existing campaign as a fresh DRAFT.
   *
   * Copies content + subject + recipients + template binding from the
   * source so the advisor can edit "last week's send" as the starting
   * point for this week's. Resets status/sentAt/compliance so the clone
   * doesn't look like an already-sent campaign and (if compliance is on)
   * has to re-enter review on its own.
   *
   * @param {string} sourceCampaignId
   * @returns {Promise<object>} the newly-created draft campaign
   * @throws if sourceCampaignId is missing or the source can't be found
   */
  async duplicateCampaign(sourceCampaignId) {
    if (!sourceCampaignId) {
      throw new Error('sourceCampaignId is required');
    }

    const source = await this.getCampaign(sourceCampaignId);
    if (!source) {
      const e = new Error(
        `CAMPAIGN_NOT_FOUND: cannot duplicate, source campaign not found: ${sourceCampaignId}`
      );
      e.code = 'CAMPAIGN_NOT_FOUND';
      throw e;
    }

    const cloneName = `${(source.name && source.name.trim()) || 'Untitled'} (Copy)`;

    // Delegate to createCampaign so we get the same counter
    // initialization, audit row, validation, and userId resolution.
    // Explicitly RESET sent/compliance state so the clone enters life
    // as a clean draft.
    return this.createCampaign({
      name: cloneName,
      // #392: do NOT copy the subject verbatim — a time-specific line like
      // "July Market Update" would silently ship in August. Blank it so the
      // advisor sets a fresh subject on Compose (which the wizard requires
      // before sending). Content + metadata are preserved for reuse.
      subject: '',
      content: source.content,
      contentType: source.contentType || 'HTML',
      templateId: source.templateId,
      seriesId: source.seriesId, // #330 — preserve series linkage on the clone
      metadata: source.metadata,
      recipientClientIds: source.recipientClientIds || [],
      recipientCount: source.recipientCount || 0,
      status: 'DRAFT',
      // sentAt: deliberately omitted (createCampaign doesn't pass it,
      // so the new row defaults to null). Explicit assertion in tests.
      complianceStatus: 'draft',
      rejectionReason: null,
    });
  }

  /**
   * #327 — bulk approve every campaign in `campaignIds`. Thin wrapper
   * around approveCampaign using Promise.allSettled so a single failure
   * doesn't block the batch — failures are reported per id.
   *
   * @param {string[]} campaignIds
   * @returns {Promise<{ succeeded: number, failed: { id: string, error: string }[] }>}
   */
  async bulkApproveCampaigns(campaignIds) {
    if (!Array.isArray(campaignIds)) {
      throw new Error('campaignIds must be an array');
    }
    if (campaignIds.length === 0) {
      return { succeeded: 0, failed: [] };
    }

    const results = await Promise.allSettled(
      campaignIds.map((id) => this.approveCampaign(id))
    );

    const failed = [];
    let succeeded = 0;
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') {
        succeeded += 1;
      } else {
        failed.push({
          id: campaignIds[i],
          error: r.reason?.message || String(r.reason),
        });
      }
    });
    return { succeeded, failed };
  }

  /**
   * #327 — bulk reject every campaign in `campaignIds` with the SAME
   * reason. Validates the reason once up front (rejectCampaign also
   * validates per call, but failing fast avoids partial writes when
   * the reason is empty).
   *
   * @param {string[]} campaignIds
   * @param {string} reason
   * @returns {Promise<{ succeeded: number, failed: { id: string, error: string }[] }>}
   */
  async bulkRejectCampaigns(campaignIds, reason) {
    if (!Array.isArray(campaignIds)) {
      throw new Error('campaignIds must be an array');
    }
    if (!reason || typeof reason !== 'string' || reason.trim().length === 0) {
      const e = new Error(
        'REJECTION_REASON_REQUIRED: rejection reason must be a non-empty string'
      );
      e.code = 'REJECTION_REASON_REQUIRED';
      throw e;
    }
    if (campaignIds.length === 0) {
      return { succeeded: 0, failed: [] };
    }

    const results = await Promise.allSettled(
      campaignIds.map((id) => this.rejectCampaign(id, reason))
    );

    const failed = [];
    let succeeded = 0;
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') {
        succeeded += 1;
      } else {
        failed.push({
          id: campaignIds[i],
          error: r.reason?.message || String(r.reason),
        });
      }
    });
    return { succeeded, failed };
  }

  /**
   * Author re-edits a rejected campaign — clears rejection state, returns to draft.
   * Idempotent: callable on already-draft campaigns (no-op, no audit row).
   * Transition: rejected → draft (or draft → draft).
   */
  async resubmitDraft(campaignId) {
    if (!campaignId) throw new Error('Campaign ID is required');
    const current = await this.getCampaign(campaignId);
    const status = current?.complianceStatus || 'draft';
    if (status === 'draft') {
      // Idempotent — return current state without writing
      return current;
    }
    return this.updateCampaign(campaignId, {
      complianceStatus: 'draft',
      rejectionReason: null,
    });
  }
}

// Export class for testing
export { EmailCampaignService };

// Export singleton instance as default
export default new EmailCampaignService();
